import { SovosBox, SovosButton } from "mosaic-react";
import React, { useEffect, useRef } from "react";
import useMappingContext from "../useMappingContext";
import ChooseEntityPageFrame from "./ChooseEntityPageFrame";
import LegalStructureSection from "./LegalStructureSection";
import MapEntitySection from "./MapEntitySection";
import { buildSuggestionScoreMap } from "./entitySuggestionScores";
import { getEntitySelectionKey } from "./entitySelectionKey";
import useEntityAiSuggestions from "./useEntityAiSuggestions";

const ChooseEntityPage = ({
    disableCancel = false,
    i18n,
    initialConfigSet,
    initialEntityName,
    initialFileType,
    onCancel,
    onCanProceedChange,
    onFetchEntities,
    onFetchMappingContexts,
    onNext,
    onRegisterProceed,
    onRetryPreload,
    onSuggestEntity,
    preloadError = false,
    profileId,
    projectId,
    showActions = false,
    projectModules,
}) => {
    const ai = useEntityAiSuggestions({ onSuggestEntity, profileId });
    const mapping = useMappingContext({
        initialConfigSet,
        initialEntityName,
        initialFileType,
        onContextReset: ai.invalidateAiState,
        onFetchEntities,
        onFetchMappingContexts,
        projectId,
        projectModules,
    });

    const showMapEntitySection = Boolean(mapping.countryCode && mapping.legalStructureId);
    const noMappableEntities =
        mapping.selectedStructure?.mappable_entity_count === 0 ||
        Boolean(
            mapping.legalStructureId &&
            !mapping.loadingEntities &&
            !mapping.entityError &&
            !mapping.entities.length,
        );
    const canProceed = Boolean(
        mapping.selectionKey &&
        mapping.entities.some(
            (entity) => getEntitySelectionKey(entity) === mapping.selectionKey,
        ),
    );

    useEffect(() => {
        onCanProceedChange?.(canProceed);
    }, [canProceed, onCanProceedChange]);

    useEffect(() => {
        if (!onRegisterProceed) return;
        onRegisterProceed(() => {
            if (!canProceed || disableCancel) return;
            onNext({
                configSet: mapping.configSet,
                entityLabel: mapping.entityName,
                entityName: mapping.entityName,
                fileType: mapping.fileType,
            });
        });
    }, [
        canProceed,
        disableCancel,
        mapping.configSet,
        mapping.entityName,
        mapping.fileType,
        onNext,
        onRegisterProceed,
    ]);

    const handleEntityChange = (value) => {
        mapping.selectEntity(value);
        const scoreBySelectionKey = buildSuggestionScoreMap(
            mapping.entities,
            ai.aiEntitySuggestions,
        );
        if (scoreBySelectionKey[value] != null) {
            ai.acceptSuggestion({
                entities: mapping.entities,
                selectionKey: value,
                suggestions: ai.aiEntitySuggestions,
            });
            return;
        }
        ai.clearEntityScore();
    };

    const handleSuggestEntity = async () => {
        const result = await ai.requestSuggestions({
            configSet: mapping.configSet,
            entities: mapping.entities,
        });
        if (result?.selectionKey) {
            mapping.selectEntity(result.selectionKey);
        }
    };

    const handleProceed = () => {
        if (!canProceed || disableCancel) return;
        onNext({
            configSet: mapping.configSet,
            entityLabel: mapping.entityName,
            entityName: mapping.entityName,
            fileType: mapping.fileType,
        });
    };

    const handleCancel = () => {
        ai.invalidateAiState();
        onCancel();
    };

    const autoSuggestKeyRef = useRef("");
    useEffect(() => {
        if (!onSuggestEntity || !showMapEntitySection) return;
        if (mapping.loadingEntities || mapping.entityError || noMappableEntities) return;
        if (mapping.unresolved) return;

        const autoSuggestKey = `${mapping.configSet}:${mapping.legalStructureId}:${mapping.entities.length}`;
        if (autoSuggestKeyRef.current === autoSuggestKey) return;
        if (mapping.selectionKey) {
            autoSuggestKeyRef.current = autoSuggestKey;
            return;
        }

        autoSuggestKeyRef.current = autoSuggestKey;
        let cancelled = false;
        (async () => {
            const result = await ai.requestSuggestions({
                configSet: mapping.configSet,
                entities: mapping.entities,
            });
            if (cancelled || !result?.selectionKey) return;
            mapping.selectEntity(result.selectionKey);
        })();

        return () => {
            cancelled = true;
        };
    }, [
        ai.requestSuggestions,
        mapping.configSet,
        mapping.entities,
        mapping.entityError,
        mapping.fileType,
        mapping.legalStructureId,
        mapping.loadingEntities,
        mapping.selectEntity,
        mapping.selectionKey,
        mapping.unresolved,
        noMappableEntities,
        onSuggestEntity,
        showMapEntitySection,
    ]);

    return (
        <ChooseEntityPageFrame>
            <LegalStructureSection
                contextError={mapping.contextError}
                contexts={mapping.contexts}
                countryCode={mapping.countryCode}
                hasNoContexts={mapping.hasNoContexts}
                i18n={i18n}
                legalStructureId={mapping.legalStructureId}
                loadingContexts={mapping.loadingContexts}
                onLoadContexts={mapping.loadContexts}
                onSelectCountry={mapping.selectCountry}
                onSelectStructure={mapping.selectStructure}
                structures={mapping.structures}
            />

            {showMapEntitySection && (
                <MapEntitySection
                    aiEntityError={ai.aiEntityError}
                    aiEntityScore={ai.aiEntityScore}
                    aiEntityWarnings={ai.aiEntityWarnings}
                    entities={mapping.entities}
                    entityError={mapping.entityError}
                    selectionKey={mapping.selectionKey}
                    i18n={i18n}
                    initialEntityName={initialEntityName}
                    initialFileType={initialFileType}
                    loadingAiEntity={ai.loadingAiEntity}
                    loadingEntities={mapping.loadingEntities}
                    noMappableEntities={noMappableEntities}
                    onReloadContexts={mapping.loadContexts}
                    onRetryEntities={mapping.retryEntities}
                    onRetryPreload={onRetryPreload}
                    onSelectEntity={handleEntityChange}
                    onSuggestEntity={onSuggestEntity ? handleSuggestEntity : undefined}
                    preloadError={preloadError}
                    selectedStructureLabel={mapping.selectedStructure?.label}
                    suggestionScores={buildSuggestionScoreMap(
                        mapping.entities,
                        ai.aiEntitySuggestions,
                    )}
                    unresolved={mapping.unresolved}
                    unresolvedEntityName={mapping.entityName}
                />
            )}

            {showActions && (
                <SovosBox sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <SovosButton disabled={disableCancel} onClick={handleCancel} variant="text">
                        {i18n`Cancel`}
                    </SovosButton>
                    <SovosButton
                        disabled={!canProceed || disableCancel}
                        onClick={handleProceed}
                        variant="contained"
                    >
                        {i18n`Next`}
                    </SovosButton>
                </SovosBox>
            )}
        </ChooseEntityPageFrame>
    );
};

export default ChooseEntityPage;
